import { Row, Col, Card, Button, Pagination, Container } from 'react-bootstrap';
import ProductCarousel from '../components/ProductCarousel';
import { useGetProductsQuery } from '../slices/productsApiSlice';
import { useGetStocksQuery } from '../slices/stocksApiSlice';
import { useGetSalesQuery } from '../slices/salesApiSlice';
import { useGetTransfersQuery } from '../slices/transfersApiSlice';
import { useGetSuppliesQuery } from '../slices/suppliesApiSlice';
import { Link, useParams } from 'react-router-dom';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Meta from '../components/Meta';
import { useDispatch } from 'react-redux';
import { addToCart } from '../slices/cartSlice';
import { useState, useEffect } from 'react';

const HomeScreen = () => {
  const { keyword } = useParams();
  const [pageNumber, setPageNumber] = useState(1);

  const { data, isLoading, error } = useGetProductsQuery({ keyword, pageNumber });

  const dispatch = useDispatch();
  const { data: stocksData } = useGetStocksQuery();
  const { data: salesData } = useGetSalesQuery();
  const { data: suppliesData } = useGetSuppliesQuery();
  const { data: transfersData } = useGetTransfersQuery();

  const [searchTerm, setSearchTerm] = useState('');
  const [qtys, setQtys] = useState({});
  const [stockBalances, setStockBalances] = useState({});
  const [showCarousel, setShowCarousel] = useState(true); // ✅ toggle state

  const handleQtyChange = (productId, value) => {
    setQtys({ ...qtys, [productId]: value });
  };

  const addToCartHandler = (product) => {
    const qty = qtys[product._id] || 0;
    dispatch(addToCart({ ...product, qty }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    if (!stocksData?.stocks || !salesData?.sales || !suppliesData?.supplies || !transfersData?.transfers) return;

    const firstDate = stocksData.stocks
      .map((item) => new Date(item.date))
      .reduce((minDate, currentDate) => (currentDate < minDate ? currentDate : minDate), new Date())
      .toISOString()
      .split('T')[0];

    const secondDate = new Date().toISOString().split('T')[0];

    const calculateMetrics = (product, type) => {
      const dataMap = {
        sales: salesData?.sales,
        supplies: suppliesData?.supplies,
        transfers: transfersData?.transfers,
      };

      const itemKeyMap = {
        sales: 'saleItems',
        supplies: 'supplyItems',
        transfers: 'transferItems',
      };

      const records = dataMap[type]?.flatMap(
        (item) =>
          item[itemKeyMap[type]]?.filter(
            (subItem) =>
              subItem.name === product.name &&
              new Date(item.date) >= new Date(firstDate) &&
              new Date(item.date) <= new Date(secondDate)
          ) || []
      );

      return records?.reduce((total, record) => total + Number(record.qty || 0), 0) || 0;
    };

    const getInitialStock = (product) =>
      stocksData?.stocks
        .flatMap(
          (item) =>
            item.stockItems?.filter(
              (subItem) => subItem.name === product.name && new Date(item.date) <= new Date(firstDate)
            ) || []
        )
        .reduce((total, subItem) => total + Number(subItem.qty || 0), 0);

    const stockBalancesTemp = {};
    data?.products?.forEach((product) => {
      const initialStock = getInitialStock(product);
      const purchases = calculateMetrics(product, 'supplies');
      const salesQty = calculateMetrics(product, 'sales');
      const transfersQty = calculateMetrics(product, 'transfers');
      const closingStock = initialStock + purchases - salesQty - transfersQty;

      stockBalancesTemp[product._id] = closingStock;
    });

    setStockBalances(stockBalancesTemp);
  }, [stocksData, salesData, suppliesData, transfersData, data]);

  const filteredProducts = data?.products?.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginationHandler = (page) => {
    setPageNumber(page);
  };

  return (
    <>
      <Meta />

      {/* Carousel Section with Toggle */}
      <Container fluid className="mb-5 text-center">
        {!keyword && (
          <>
            <Button
              variant={showCarousel ? 'secondary' : 'primary'}
              className="mb-3"
              onClick={() => setShowCarousel(!showCarousel)}
            >
              {showCarousel ? 'Hide Carousel' : 'Show Carousel'}
            </Button>

            {showCarousel && (
              <div className="bg-light rounded shadow-sm p-3">
                <h2 className="text-center mb-4">Featured Products</h2>
                <ProductCarousel />
              </div>
            )}
          </>
        )}
        {keyword && (
          <Link to="/" className="btn btn-light mb-4">
            Go Back
          </Link>
        )}
      </Container>

      {searchTerm && (
        <Link to="/" className="btn btn-light mb-4">
          Go Back
        </Link>
      )}

      {/* Search Box */}
      <div className="mb-4 text-center">
        <input
          type="text"
          placeholder="Search products..."
          className="form-control"
          value={searchTerm}
          onChange={handleSearch}
          style={{ maxWidth: '400px', margin: '0 auto' }}
        />
      </div>

      {/* Products Section */}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || error.error}</Message>
      ) : (
        <>
          <h1 className="mb-4">Products</h1>
          <Row>
            {filteredProducts?.map((product) => (
              <Col key={product._id} xs={12} sm={6} md={6} lg={3}>
                <Card className="mb-4 shadow-sm">
                  {product.images && product.images.length > 0 && (
                    <Card.Img
                      variant="top"
                      src={product.images[0]}
                      alt={`Product ${product.name}`}
                      style={{
                        width: '75%',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '10px',
                        margin: '0 auto',
                      }}
                    />
                  )}
                  <Card.Body>
                    <Card.Title>{product.name}</Card.Title>
                    <Card.Title>D {product.price}</Card.Title>
                    <Card.Text style={{ display: 'none' }}>
                      Price: D{product.price} <br />
                      Balance: {stockBalances[product._id] ?? 'Calculating...'}
                    </Card.Text>
                    <Card.Text>
                      Brand: {product.brand} | Category: {product.category}
                    </Card.Text>

                    {/* Quantity Adjustment */}
                    <div className="d-flex align-items-center mb-2">
                      <Button
                        variant="light"
                        className="btn-sm"
                        onClick={() =>
                          handleQtyChange(
                            product._id,
                            (qtys[product._id] || 0) > 0 ? qtys[product._id] - 1 : 0
                          )
                        }
                        disabled={(qtys[product._id] || 0) === 0}
                      >
                        -
                      </Button>
                      <span className="mx-2">{qtys[product._id] || 0}</span>
                      <Button
                        variant="light"
                        className="btn-sm"
                        onClick={() =>
                          handleQtyChange(
                            product._id,
                            (qtys[product._id] || 0) < product.countInStock
                              ? (qtys[product._id] || 0) + 1
                              : product.countInStock
                          )
                        }
                        disabled={(qtys[product._id] || 0) === product.countInStock}
                      >
                        +
                      </Button>
                    </div>

                    {/* Add to Cart */}
                    <Button
                      variant="warning"
                      className="btn-sm"
                      onClick={() => addToCartHandler(product)}
                      disabled={(qtys[product._id] || 0) === 0 || product.countInStock === 0}
                    >
                      Add To Cart
                    </Button>
                    <Link
                      className="btn btn-primary btn-sm m-2"
                      to={`/product/${product._id}`}
                      role="button"
                    >
                      Details
                    </Link>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

                    {/* Pagination */}
          <Pagination className="justify-content-center mt-4">
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default HomeScreen;